/**
 * SizilPro — WizardStep2ClientInfo
 * Step 2 of the proposal wizard: client name, email, and address.
 *
 * Updated to better match the current template → proposal handoff:
 * - reminds the user the selected template is already shaping the proposal
 * - surfaces builder mode + fixed-page direction
 * - keeps the step lightweight and easy to complete
 *
 * File: src/modules/proposal-builder/components/wizard/WizardStep2ClientInfo.tsx
 */

import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Loader2,
  ArrowLeft,
  ArrowRight,
  User,
  Mail,
  MapPin,
  LayoutTemplate,
  Move,
  Pin,
  PanelsTopLeft,
  Wrench,
  FileStack,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { BuilderMode, FixedPagePolicy } from "../../types";
import type { WizardAnswers } from "../../hooks/useProposalWizard";

const schema = z.object({
  prospectName: z.string().min(1, "Client name is required"),
  prospectEmail: z
    .string()
    .email("Enter a valid email")
    .optional()
    .or(z.literal("")),
  prospectAddress: z.string().optional(),
  prospectCity: z.string().optional(),
  prospectState: z.string().optional(),
  prospectZip: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

const BUILDER_MODE_CONFIG: Record<
  BuilderMode,
  {
    label: string;
    icon: React.ReactNode;
    color: string;
    description: string;
  }
> = {
  prebuilt: {
    label: "Pre-built",
    icon: <LayoutTemplate className="h-4 w-4" />,
    color: "text-cyan-300 bg-cyan-500/10 border-cyan-500/20",
    description:
      "This proposal is starting from a simpler reusable template blueprint.",
  },
  drag_drop: {
    label: "Drag & drop",
    icon: <Move className="h-4 w-4" />,
    color: "text-fuchsia-300 bg-fuchsia-500/10 border-fuchsia-500/20",
    description:
      "This proposal is starting from a template marked for the richer builder direction.",
  },
};

const FIXED_PAGE_POLICY_CONFIG: Record<
  FixedPagePolicy,
  {
    label: string;
    icon: React.ReactNode;
    color: string;
    description: string;
  }
> = {
  template_defined: {
    label: "Template pages",
    icon: <PanelsTopLeft className="h-4 w-4" />,
    color: "text-white/45 bg-white/5 border-white/10",
    description:
      "This template mainly controls its own included page mix.",
  },
  brand_defined: {
    label: "Brand-defined fixed pages",
    icon: <Pin className="h-4 w-4" />,
    color: "text-amber-300 bg-amber-500/10 border-amber-500/20",
    description:
      "This proposal will inherit fixed pages defined in Brand Setup.",
  },
  mixed: {
    label: "Mixed fixed pages",
    icon: <Pin className="h-4 w-4" />,
    color: "text-emerald-300 bg-emerald-500/10 border-emerald-500/20",
    description:
      "This proposal will inherit both template-selected pages and brand-defined fixed pages.",
  },
};

interface Props {
  answers: WizardAnswers;
  isCreating: boolean;
  onSubmit: (values: FormValues) => void;
  onBack: () => void;
}

export function WizardStep2ClientInfo({
  answers,
  isCreating,
  onSubmit,
  onBack,
}: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      prospectName: answers.prospectName,
      prospectEmail: answers.prospectEmail,
      prospectAddress: answers.prospectAddress,
      prospectCity: answers.prospectCity,
      prospectState: answers.prospectState,
      prospectZip: answers.prospectZip,
    },
  });

  const builderCfg =
    BUILDER_MODE_CONFIG[answers.templateBuilderMode] ??
    BUILDER_MODE_CONFIG.prebuilt;

  const fixedCfg =
    FIXED_PAGE_POLICY_CONFIG[answers.templateFixedPagePolicy] ??
    FIXED_PAGE_POLICY_CONFIG.template_defined;

  const templateName = answers.template?.name ?? "Selected template";
  const templateDescription =
    answers.template?.description?.trim() ||
    "This template is already shaping the proposal structure, service defaults, and page direction.";

  const serviceCount = answers.lineItems.length;
  const pageCount = Array.from(
    new Set(
      answers.templateStaticPageIds.filter(
        (pageId): pageId is string =>
          typeof pageId === "string" && pageId.trim().length > 0
      )
    )
  ).length;
  const isEditMode = Boolean(
    answers.prospectName || answers.prospectEmail || answers.projectContext
  );

  const prospectAddress = watch("prospectAddress") ?? "";

  const handleAddressSelect = useCallback(
    (parsed: { street: string; city: string; state: string; zip: string }) => {
      setValue("prospectCity", parsed.city);
      setValue("prospectState", parsed.state);
      setValue("prospectZip", parsed.zip);
    },
    [setValue]
  );

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
        <p className="text-xs uppercase tracking-[0.14em] text-white/30">
          Template handoff
        </p>
        <h2 className="mt-2 text-lg font-semibold text-white">{templateName}</h2>
        <p className="mt-2 text-sm leading-6 text-white/45">
          {templateDescription}
        </p>

        <div className="mt-4 flex flex-wrap gap-2">
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
              builderCfg.color
            )}
          >
            {builderCfg.icon}
            {builderCfg.label}
          </span>

          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
              fixedCfg.color
            )}
          >
            {fixedCfg.icon}
            {fixedCfg.label}
          </span>

          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-white/40">
            <Wrench className="h-3.5 w-3.5" />
            {serviceCount} service{serviceCount !== 1 ? "s" : ""}
          </span>

          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs text-white/40">
            <FileStack className="h-3.5 w-3.5" />
            {pageCount} page{pageCount !== 1 ? "s" : ""}
          </span>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <InfoCard
            title="Builder direction"
            description={builderCfg.description}
          />
          <InfoCard
            title="Fixed page direction"
            description={fixedCfg.description}
          />
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="rounded-xl border border-white/10 bg-black/20 px-5 py-4">
          <p className="text-sm font-medium text-white/80">
            {isEditMode ? "Update the prospect details" : "Add the prospect details"}
          </p>
          <p className="mt-1 text-sm leading-6 text-white/45">
            This information personalizes the proposal cover, customer-facing language,
            property location, and final send/sign flow.
          </p>
        </div>

        <FieldGroup
          icon={<User className="h-4 w-4" />}
          label="Client or company name"
          required
          hint="This appears on the proposal and personalizes the customer-facing copy."
          error={errors.prospectName?.message}
        >
          <Input
            placeholder="Ocean Prime Restaurant"
            autoFocus
            error={!!errors.prospectName}
            {...register("prospectName")}
          />
        </FieldGroup>

        <FieldGroup
          icon={<Mail className="h-4 w-4" />}
          label="Email address"
          hint="We'll use this when sending the proposal."
          error={errors.prospectEmail?.message}
        >
          <Input
            type="email"
            placeholder="owner@oceanprime.com"
            error={!!errors.prospectEmail}
            {...register("prospectEmail")}
          />
        </FieldGroup>

        <FieldGroup
          icon={<MapPin className="h-4 w-4" />}
          label="Client address"
          hint="The client's address — appears on the proposal cover and anchors the job location."
        >
          <div className="space-y-2">
            <AddressAutocomplete
              value={prospectAddress}
              onChange={(v) => setValue("prospectAddress", v)}
              onSelect={handleAddressSelect}
            />
            <div className="grid grid-cols-5 gap-2">
              <div className="col-span-3">
                <Input placeholder="City" {...register("prospectCity")} />
              </div>
              <Input placeholder="ST" {...register("prospectState")} />
              <Input placeholder="ZIP" {...register("prospectZip")} />
            </div>
          </div>
        </FieldGroup>

        <div className="rounded-xl border border-amber-500/15 bg-amber-500/5 px-5 py-4">
          <p className="text-sm font-medium text-amber-300">
            What happens next
          </p>
          <p className="mt-1 text-sm leading-6 text-white/45">
            After this step, you’ll add the project details. Then the wizard will
            use your selected template plus this customer information to draft the
            proposal summary and service scope.
          </p>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
          <p className="text-xs uppercase tracking-[0.14em] text-white/30">
            Proposal flow
          </p>
          <div className="mt-3 grid gap-3 md:grid-cols-3">
            <InfoCard
              title="Template"
              description="Inherited services, pages, builder mode, and AI context are already in place."
            />
            <InfoCard
              title="Client info"
              description="This step personalizes who the proposal is for and where the work is happening."
            />
            <InfoCard
              title="Project context"
              description="Next, you'll describe the job so AI can draft the summary and service scope."
            />
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            onClick={onBack}
            disabled={isCreating}
            className="flex items-center gap-1.5 px-4 py-2.5 text-sm text-white/40 transition-colors hover:text-white/70 disabled:opacity-30"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>

          <button
            type="submit"
            disabled={isCreating}
            className="flex items-center gap-2 rounded-xl bg-white px-6 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-white/90 disabled:opacity-50"
          >
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Preparing...
              </>
            ) : (
              <>
                <ArrowRight className="h-4 w-4" />
                Continue
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

function InfoCard({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
      <p className="text-[11px] uppercase tracking-[0.14em] text-white/30">
        {title}
      </p>
      <p className="mt-2 text-sm leading-6 text-white/45">{description}</p>
    </div>
  );
}

function FieldGroup({
  icon,
  label,
  required,
  hint,
  error,
  children,
}: {
  icon?: React.ReactNode;
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        {icon && <span className="text-white/30">{icon}</span>}
        <label className="text-sm font-medium text-white/70">
          {label}
          {required && <span className="ml-1 text-red-400">*</span>}
        </label>
      </div>
      {hint && <p className="-mt-1 text-xs text-white/30">{hint}</p>}
      {children}
      {error && <p className="text-xs text-red-400">{error}</p>}
    </div>
  );
}

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  error?: boolean;
};

const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { error, className, ...props },
  ref
) {
  return (
    <input
      ref={ref}
      className={cn(
        "w-full rounded-lg border bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/20",
        "focus:outline-none focus:ring-1 transition-colors",
        error
          ? "border-red-500/40 focus:ring-red-500/30"
          : "border-white/10 focus:border-white/20 focus:ring-white/20",
        className
      )}
      {...props}
    />
  );
});

// ─── Address autocomplete (OpenStreetMap Nominatim — no API key needed) ─────

interface NominatimResult {
  place_id: number;
  display_name: string;
  address: {
    house_number?: string;
    road?: string;
    city?: string;
    town?: string;
    village?: string;
    county?: string;
    state?: string;
    postcode?: string;
  };
}

interface ParsedAddress {
  street: string;
  city: string;
  state: string;
  zip: string;
}

function parseNominatim(result: NominatimResult): ParsedAddress {
  const a = result.address;
  const street = [a.house_number, a.road].filter(Boolean).join(" ");
  return {
    street,
    city: a.city ?? a.town ?? a.village ?? a.county ?? "",
    state: a.state ?? "",
    zip: a.postcode ?? "",
  };
}

function AddressAutocomplete({
  value,
  onChange,
  onSelect,
}: {
  value: string;
  onChange: (v: string) => void;
  onSelect: (parsed: ParsedAddress) => void;
}) {
  const [suggestions, setSuggestions] = useState<NominatimResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const skipFetch = useRef(false);

  // Close dropdown on outside click
  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, []);

  // Debounced Nominatim fetch
  useEffect(() => {
    if (skipFetch.current) {
      skipFetch.current = false;
      return;
    }
    if (!value || value.length < 3) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }
    setIsLoading(true);
    const timer = setTimeout(() => {
      const params = new URLSearchParams({
        q: value,
        format: "json",
        addressdetails: "1",
        limit: "5",
        countrycodes: "us",
      });
      fetch(`https://nominatim.openstreetmap.org/search?${params}`, {
        headers: { "Accept-Language": "en" },
      })
        .then((res) => res.json() as Promise<NominatimResult[]>)
        .then((data) => {
          setSuggestions(data);
          setIsOpen(data.length > 0);
          setHighlightedIndex(-1);
        })
        .catch(() => {
          setSuggestions([]);
          setIsOpen(false);
        })
        .finally(() => setIsLoading(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [value]);

  const handleSelect = useCallback(
    (result: NominatimResult) => {
      const parsed = parseNominatim(result);
      skipFetch.current = true;
      onChange(parsed.street);
      onSelect(parsed);
      setIsOpen(false);
      setSuggestions([]);
    },
    [onChange, onSelect]
  );

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!isOpen) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[highlightedIndex]);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => suggestions.length > 0 && setIsOpen(true)}
          placeholder="123 Main Street"
          autoComplete="off"
          className={cn(
            "w-full rounded-lg border bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/20",
            "focus:outline-none focus:ring-1 transition-colors",
            "border-white/10 focus:border-white/20 focus:ring-white/20",
            isLoading && "pr-10"
          )}
        />
        {isLoading && (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-white/30" />
          </span>
        )}
      </div>

      {isOpen && suggestions.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full overflow-hidden rounded-xl border border-white/15 bg-[#1a1a1e] shadow-xl">
          {suggestions.map((result, index) => (
            <li key={result.place_id}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(result);
                }}
                className={cn(
                  "flex w-full items-start gap-3 px-4 py-3 text-left text-sm transition-colors",
                  index === highlightedIndex
                    ? "bg-white/10 text-white"
                    : "text-white/60 hover:bg-white/5 hover:text-white/90"
                )}
              >
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-white/30" />
                <span className="line-clamp-2">{result.display_name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}